import { useEffect } from "react";
import { Link } from "react-router-dom";
import LegalLayout from "@/components/LegalLayout";

export default function Terms() {
  useEffect(() => {
    document.title = "Terms of Service · Stable-ish";
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalLayout
      title="Terms of Service"
      eyebrow="§ legal · terms"
      lastUpdated="Effective Date: July 26, 2026"
    >
      <p>
        Welcome to Stable-ish. These Terms of Service (&quot;Terms&quot;) are a
        legal agreement between you and <strong>Stable-ish LLC</strong>
        (&quot;Stable-ish,&quot; &quot;we,&quot; &quot;us,&quot; or
        &quot;our&quot;). By downloading, accessing, or using the Stable-ish
        application and related services (collectively, the
        &quot;Service&quot;), you agree to be bound by these Terms. If you do
        not agree to these Terms, do not use the Service.
      </p>

      <h2>1. Intended Users</h2>
      <p>
        Stable-ish is an educational clinical reference tool designed primarily
        for licensed emergency department and critical care registered nurses
        and other trained healthcare professionals.
      </p>
      <p>
        The Service is not intended for use by the general public as a
        substitute for professional medical advice or clinical decision-making.
      </p>

      <h2>2. Educational Purpose Only</h2>
      <p>
        Stable-ish provides educational and informational content for reference
        purposes only.
      </p>
      <p>
        The Service does not provide medical advice, nursing advice, pharmacy
        advice, legal advice, diagnosis, treatment recommendations, or
        professional consultation.
      </p>
      <p>Information provided through the Service must not be used as a substitute for:</p>
      <ul>
        <li>Professional clinical judgment</li>
        <li>Provider orders</li>
        <li>Hospital policies and procedures</li>
        <li>Pharmacist consultation</li>
        <li>Manufacturer guidance</li>
        <li>Applicable laws, regulations, or licensing requirements</li>
      </ul>
      <p>Users remain solely responsible for all clinical decisions and actions.</p>

      <h2>3. AI-Generated Content</h2>
      <p>Stable-ish uses artificial intelligence technologies to generate responses.</p>
      <p>
        AI-generated content may be inaccurate, incomplete, outdated,
        misleading, or incorrect.
      </p>
      <p>
        Users must independently verify all information before relying upon it
        in any professional, educational, or clinical setting.
      </p>
      <p>
        The Service should never be relied upon as the sole basis for patient
        care decisions.
      </p>

      <h2>4. Not a Medical Device</h2>
      <p>Stable-ish is intended solely as an educational and reference tool.</p>
      <p>
        Stable-ish is not a medical device and is not intended to diagnose,
        treat, cure, prevent, monitor, or manage any disease or medical
        condition.
      </p>
      <p>
        The Service is not intended to replace professional medical judgment or
        clinical expertise.
      </p>

      <h2>5. Prohibited Content</h2>
      <p>
        Users agree not to submit, upload, transmit, or otherwise provide:
      </p>
      <ul>
        <li>Protected Health Information (PHI)</li>
        <li>Patient identifiers</li>
        <li>Medical record numbers (MRNs)</li>
        <li>Dates of birth</li>
        <li>Patient names</li>
        <li>Confidential patient information</li>
        <li>Content that violates applicable laws or regulations</li>
        <li>Content that infringes the rights of others</li>
      </ul>
      <p>
        Users are solely responsible for ensuring that submitted content
        complies with applicable privacy laws, employer policies, and
        professional obligations.
      </p>
      <p>
        Stable-ish may provide safeguards intended to reduce the submission of
        Protected Health Information; however, such safeguards are not
        guaranteed to identify or remove all PHI.
      </p>

      <h2>6. User Responsibilities</h2>
      <p>By using the Service, you agree to:</p>
      <ul>
        <li>Exercise independent professional judgment</li>
        <li>Verify information before relying upon it</li>
        <li>Follow applicable laws and regulations</li>
        <li>Follow employer policies and procedures</li>
        <li>Use the Service only for lawful purposes</li>
        <li>Maintain responsibility for all professional decisions and actions</li>
      </ul>
      <p>
        You acknowledge that healthcare information changes rapidly and that
        information available through the Service may become outdated.
      </p>

      <h2>7. Uploaded Content</h2>
      <p>
        Certain features may allow users to submit images, documents,
        screenshots, ECGs, monitor strips, or audio recordings.
      </p>
      <p>Users remain solely responsible for:</p>
      <ul>
        <li>Ensuring submitted content does not contain PHI</li>
        <li>Reviewing content before submission</li>
        <li>Verifying the accuracy and completeness of submitted materials</li>
        <li>Independently evaluating any AI-generated analysis or interpretation</li>
      </ul>
      <p>
        Stable-ish does not independently verify the quality, completeness,
        authenticity, calibration, orientation, or clinical significance of
        uploaded materials.
      </p>

      <h2>8. Intellectual Property</h2>
      <p>
        All content, software, trademarks, logos, designs, text, graphics, and
        other materials associated with Stable-ish are owned by or licensed to
        Stable-ish and are protected by applicable intellectual property laws.
      </p>
      <p>Users may not:</p>
      <ul>
        <li>Copy</li>
        <li>Reproduce</li>
        <li>Distribute</li>
        <li>Modify</li>
        <li>Reverse engineer</li>
        <li>Create derivative works from</li>
      </ul>
      <p>
        any portion of the Service except as expressly permitted by law.
      </p>

      <h2>9. Privacy</h2>
      <p>
        Use of the Service is also governed by the Stable-ish{" "}
        <Link to="/privacy" className="text-[#FF2A2A]">
          Privacy Policy
        </Link>
        , which is incorporated into these Terms by reference.
      </p>

      <h2>10. Disclaimer of Warranties</h2>
      <p>THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot;</p>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, STABLE-ISH DISCLAIMS ALL
        WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY,
        FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, RELIABILITY, NON-INFRINGEMENT,
        AND AVAILABILITY.
      </p>
      <p>WE DO NOT WARRANT THAT:</p>
      <ul>
        <li>THE SERVICE WILL BE ERROR-FREE</li>
        <li>INFORMATION WILL ALWAYS BE ACCURATE OR CURRENT</li>
        <li>THE SERVICE WILL BE UNINTERRUPTED</li>
        <li>DEFECTS WILL BE CORRECTED</li>
        <li>THE SERVICE WILL MEET YOUR EXPECTATIONS</li>
      </ul>

      <h2>11. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, STABLE-ISH, ITS OWNERS,
        DEVELOPERS, AFFILIATES, CONTRACTORS, LICENSORS, AND REPRESENTATIVES
        SHALL NOT BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
        CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES ARISING FROM OR RELATED
        TO:
      </p>
      <ul>
        <li>USE OF THE SERVICE</li>
        <li>RELIANCE ON INFORMATION PROVIDED THROUGH THE SERVICE</li>
        <li>AI-GENERATED CONTENT</li>
        <li>CLINICAL DECISIONS</li>
        <li>PATIENT OUTCOMES</li>
        <li>MEDICATION ERRORS</li>
        <li>LOSS OF DATA</li>
        <li>BUSINESS INTERRUPTION</li>
      </ul>
      <p>YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK.</p>

      <h2>12. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless Stable-ish and its
        owners, affiliates, contractors, licensors, and representatives from
        and against any claims, liabilities, damages, losses, costs, and
        expenses arising from:
      </p>
      <ul>
        <li>Your use of the Service</li>
        <li>Your violation of these Terms</li>
        <li>Your submission of prohibited content</li>
        <li>Your professional decisions or actions</li>
        <li>Your misuse of information obtained through the Service</li>
      </ul>

      <h2>13. Suspension and Termination</h2>
      <p>
        We reserve the right to suspend, restrict, or terminate access to the
        Service at any time, with or without notice, if we believe these Terms
        have been violated or if continued access may pose legal, security,
        operational, or safety concerns.
      </p>

      <h2>14. Changes to These Terms</h2>
      <p>We may modify these Terms from time to time.</p>
      <p>
        Updated Terms become effective upon posting within the Service or on
        our website.
      </p>
      <p>
        Continued use of the Service after changes are posted constitutes
        acceptance of the revised Terms.
      </p>

      <h2>15. Eligibility &amp; Age Requirement</h2>
      <p>
        You must be at least <strong>18 years of age</strong> to create an
        account and use Stable-ish. By using the Service you represent and
        warrant that you are either:
      </p>
      <ul>
        <li>
          A currently-licensed healthcare professional (registered nurse,
          nurse practitioner, physician, physician assistant, paramedic,
          respiratory therapist, or similar), <em>or</em>
        </li>
        <li>
          A student enrolled in an accredited healthcare training program
          preparing for licensure in one of the above roles.
        </li>
      </ul>
      <p>
        The Service is not designed or intended for use by patients or the
        general public. If you do not meet these criteria, please do not use
        Stable-ish.
      </p>

      <h2>16. Subscription, Billing, and Cancellation</h2>
      <p>
        Stable-ish offers a paid subscription that unlocks the full clinical
        toolkit after a free trial. All subscriptions are billed through
        Apple&rsquo;s In-App Purchase system.
      </p>
      <ul>
        <li>
          <strong>Free trial.</strong> New subscribers receive a{" "}
          <strong>14-day free trial</strong>. You may cancel at any point
          during the trial and will not be charged.
        </li>
        <li>
          <strong>Pricing.</strong> After the trial, subscriptions are billed
          at <strong>US$9.99 per month</strong> or <strong>US$79.99 per year</strong>{" "}
          (the annual plan is offered as a &ldquo;Founding Member Rate&rdquo;
          during our launch period).
        </li>
        <li>
          <strong>Founding Member Rate.</strong> Subscribers who lock in a
          Founding Member Rate during the launch period retain that same
          price for the lifetime of their continuous subscription, per
          Apple&rsquo;s in-app purchase price-lock policy. If your
          subscription lapses and you later resubscribe, the then-current
          public price will apply.
        </li>
        <li>
          <strong>Payment.</strong> Payment is charged to your{" "}
          <strong>Apple ID</strong> account at confirmation of purchase.
          Stable-ish LLC never sees your credit card, bank account, or Apple
          ID password.
        </li>
        <li>
          <strong>Auto-renewal.</strong> Your subscription{" "}
          <strong>automatically renews</strong> at the end of each billing
          period at the same rate unless you turn off auto-renew at least{" "}
          <strong>24 hours before the end of the current period</strong>.
          Your Apple ID account will be charged for renewal within 24 hours
          prior to the end of the current period.
        </li>
        <li>
          <strong>Managing or cancelling your subscription.</strong> You can
          manage your subscription or turn off auto-renewal at any time by
          going to{" "}
          <strong>Settings → [your name] → Subscriptions</strong> on your
          iPhone or iPad. Uninstalling the Stable-ish app does not cancel
          your subscription.
        </li>
        <li>
          <strong>No refunds for unused portions.</strong> Except where
          required by applicable law, we do not offer refunds or credits for
          partial subscription periods, unused features, or portions of the
          subscription that remain unused if you cancel mid-period. Refund
          requests are administered by Apple through{" "}
          <a
            href="https://support.apple.com/en-us/HT204084"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#FF2A2A]"
          >
            reportaproblem.apple.com
          </a>{" "}
          under Apple&rsquo;s standard App Store refund policy — Stable-ish
          LLC has no ability to issue refunds directly.
        </li>
        <li>
          <strong>Restore purchases.</strong> If you subscribe on one device
          and later install Stable-ish on another device signed in to the
          same Apple ID, you can restore your subscription by tapping{" "}
          <em>Restore Purchases</em> on the paywall screen inside the app.
        </li>
        <li>
          <strong>Price changes.</strong> We may change the price of new
          subscriptions from time to time. Existing subscribers will be
          notified through Apple&rsquo;s standard subscription-price-change
          flow and given the opportunity to accept or cancel before any
          price increase takes effect.
        </li>
      </ul>

      <h2>17. Governing Law</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the
        laws of the <strong>State of Connecticut</strong>, without regard to
        conflict-of-law principles. Any dispute arising out of or related to
        these Terms or the Service shall be brought exclusively in the state
        or federal courts located in Hartford County, Connecticut.
      </p>

      <h2>18. Contact Information</h2>
      <p>
        <strong>Questions regarding these Terms of Service:</strong>
        <br />
        <a href="mailto:support@stableish.me">support@stableish.me</a>
      </p>
      <p>
        <strong>Privacy Questions and Data Requests:</strong>
        <br />
        <a href="mailto:privacy@stableish.me">privacy@stableish.me</a>
      </p>

      <h2>19. Acknowledgment</h2>
      <p>
        By accessing or using Stable-ish, you acknowledge that you have read,
        understood, and agree to be bound by these Terms of Service, our{" "}
        <Link to="/privacy" className="text-[#FF2A2A]">Privacy Policy</Link>, and our{" "}
        <Link to="/medical-disclaimer" className="text-[#FF2A2A]">Medical Disclaimer</Link>.
      </p>
    </LegalLayout>
  );
}
